<template>
  <div class="credit-dashboard-wrapper tw-px-6">
    <div class="back-btn">
      <router-link :to="{ name: 'CreditProvider' }">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.828 7.00017H16V9.00017H3.828L9.192 14.3642L7.778 15.7782L0 8.00017L7.778 0.222168L9.192 1.63617L3.828 7.00017Z"
            fill="#1A2139"
          />
        </svg>
      </router-link>

      <h1>Borrower’s Information</h1>
    </div>

    <div>
      <h1 class="loan-details-title">Loan Details</h1>
      <p class="loan-details-subtitle">View loan information</p>
    </div>

    <div class="credit-container tw-py-4">
      <div v-if="isLoading" class="tw-text-center tw-py-4">
        Loading borrower data...
      </div>

      <CreditBanner
        v-else
        :borrowerName="formattedBorrowerName"
        :totalCreditUnit="formattedTotalCreditUnit || '0.00'"
        :totalLoaned="formattedTotalLoaned || '0.00'"
      />

      <div class="tw-mt-6">
        <div
          class="tw-border-b tw-flex tw-gap-4 tw-mb-4"
          :style="{ width: 'fit-content' }"
        >
          <button
            class="tw-px-4 tw-py-2 tw-font-medium hover:tw-bg-transparent text-hover"
            :class="
              activeTab === 'all'
                ? 'tw-border-b-2 tw-border-blue-500 tw-text-[#1A2139]'
                : 'tw-text-[#8791A7]'
            "
            @click="activeTab = 'all'"
          >
            All
          </button>
          <button
            class="tw-px-4 tw-py-2 tw-font-medium hover:tw-bg-transparent text-hover"
            :class="
              activeTab === 'active'
                ? 'tw-border-b-2 tw-border-blue-500 tw-text-[#1A2139]'
                : 'tw-text-[#8791A7]'
            "
            @click="activeTab = 'active'"
          >
            Active
          </button>
        </div>

        <ActiveLoansTable
          :loans="displayedLoans"
          @repay="handleLoanRepay"
          @loanDetails="handleLoanDetails"
        />
      </div>
    </div>
  </div>

  <RepayModal
    v-if="showCreditModal"
    :title="modalTitle"
    :subTitle="creditSubTitle"
    :form="creditForm"
    @close="closeModal"
    @goBack="goBack"
  >
    <template v-slot:content>
      <CreditLoanDetails
        v-if="creditForm === 'CreditLoanDetails'"
        class="tw-px-4 lg:tw-px-10"
        :loanDetails="selectedLoanDetails"
      />
    </template>
  </RepayModal>
</template>
  
<script>
import CreditBanner from "@/components/creditProvider/CreditBanner.vue";
import ActiveLoansTable from "@/components/tables/creditProvider/ActiveLoansTable.vue";
import RepayModal from "@/components/general/sidemodal/SideModal.vue";
import CreditLoanDetails from "@/components/creditProvider/CreditLoanDetails.vue";
import api from "@/utils/api.js";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "CreditBorrower",
  components: {
    CreditBanner,
    ActiveLoansTable,
    RepayModal,
    CreditLoanDetails,
  },

  data() {
    return {
      activeTab: "all",
      isLoading: true,
      borrowerId: "",
      borrowerData: null,
      modalTitle: "",
      creditForm: "",
      creditSubTitle: "",
      showCreditModal: false,
      selectedLoanDetails: {},
      paybackTransactions: [],
    };
  },

  computed: {
    ...mapGetters({
      allBorrowers: "organisationModule/borrowers",
      // repaymentHistory: "creditModule/repaymentHistory",
    }),

    borrowerLoans() {
      if (this.allBorrowers.length < 1 || !this.borrowerId) return [];

      const borrower = this.allBorrowers.find(
        (borrower) => borrower && borrower._id === this.borrowerId
      );

      return borrower ? borrower.loans : [];
    },

    activeLoans() {
      return this.borrowerLoans.filter((loan) => loan.status === "ACTIVE");
    },

    displayedLoans() {
      return this.activeTab === "active"
        ? this.activeLoans
        : this.borrowerLoans;
    },

    formattedBorrowerName() {
      if (this.allBorrowers.length === 0) {
        return "Unknown Borrower";
      }

      const borrower = this.allBorrowers[0];
      return borrower
        ? `${borrower.fname} ${borrower.lname}`
        : "Unknown Borrower";
    },

    formattedTotalCreditUnit() {
      if (!this.borrowerLoans || this.borrowerLoans.length === 0) {
        return new Intl.NumberFormat("en-NG", {
          style: "currency",
          currency: "NGN",
          minimumFractionDigits: 2,
        }).format(0);
      }

      const creditUnits = this.borrowerLoans.map((loan) => {
        if (!loan.collaterals || !Array.isArray(loan.collaterals)) {
          return 0;
        }

        return loan.collaterals.reduce((total, curr) => {
          const price = curr?.token?.price?.buy || 0;
          const quantity = curr?.quantity || 0;
          return total + (price * quantity);
        }, 0);
      });

      const totalCreditUnit = creditUnits.reduce(
        (sum, value) => sum + value,
        0
      );

      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        minimumFractionDigits: 2,
      }).format(totalCreditUnit);
    },

    formattedTotalLoaned() {
      const total = this.borrowerLoans.reduce(
        (total, loan) => total + (parseFloat(loan.amount) || 0),
        0
      );

      return total.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },

  methods: {
    // ...mapActions({
    //   getRepaymentHistory: "creditModule/getRepaymentHistory",
    // }),

    ...mapActions("organisationModule", ["getBorrowers"]),

    async loadInitialData() {
      this.isLoading = true;

      try {
        this.borrowerId = this.$route.params.id;

        if (!this.borrowerId) {
          console.error("Borrower ID is missing from route params");
          this.handleError(new Error("Borrower ID is missing"));
          return;
        }

        await Promise.all([
          this.getBorrowers(),
          this.fetchPaybackTransactions(),
          // this.getRepaymentHistory({ page: 1, limit: 10 }),
        ]);

        if (this.borrowerLoans.length === 0) {
          console.warn(`No loans found for borrower ID: ${this.borrowerId}`);
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleError(error) {
      console.error("Error loading borrower data:", error);
    },

    handleLoanRepay() {},

    openCreditModal(btn) {
      switch (btn) {
        case "credit":
        case "loanDetails":
          this.showCreditModal = !this.showCreditModal;
          this.modalTitle = `Loan Details`;
          this.creditForm = "CreditLoanDetails";
          this.bgChange = false;
          break;
        default:
          break;
      }
    },

    closeModal() {
      this.showCreditModal = !this.showCreditModal;
    },

    goBack() {
      this.showCreditModal = !this.showCreditModal;
    },

    handleLoanDetails(loan) {
      const details = { ...loan };

      // If the loan has been repaid, its repayment transaction holds the actual
      // amount repaid, so surface that in the details modal.
      const repay = this.findRepayForLoan(loan);
      if (repay) {
        const amount = repay.price
          ? repay.price * repay.volume
          : (repay.token?.price?.buy || 0) * repay.volume || Number(repay.volume);
        if (amount) details.repayment_amount = amount;
      }

      this.selectedLoanDetails = details;
      this.openCreditModal("loanDetails");
    },

    async fetchPaybackTransactions() {
      try {
        const response = await api.getTransactions({
          page: 1,
          limit: 1000,
          type: "LOAN_PAYBACK_LIEN",
        });
        this.paybackTransactions = response?.data?.data?.transactions || [];
      } catch (error) {
        console.error("Error fetching loan repayment transactions:", error);
      }
    },

    // Match a repayment transaction to a loan via the loan id it carries.
    findRepayForLoan(loan) {
      const loanIds = [loan._id, loan.id].filter(Boolean).map(String);
      return this.paybackTransactions.find((tx) => {
        const txLoanId = tx.loan?._id || tx.loan?.$oid || tx.loan;
        return txLoanId && loanIds.includes(String(txLoanId));
      });
    },
  },

  created() {
    this.loadInitialData();
  },
};
</script>

<style lang="scss" scoped>
.text-hover:hover {
  color: #333;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1a2139;
  font-size: 18px;
  margin-bottom: 20px;
  font-weight: bold;
}

.back-btn a {
  padding: 10px;
  border-radius: 100%;
  background-color: #d9dce8;
}

.loan-details-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a2139;
  margin: 0 0 8px 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.loan-details-subtitle {
  font-size: 16px;
  font-weight: 400;
  color: #323e67;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .loan-details-title {
    font-size: 16px;
  }

  .loan-details-subtitle {
    font-size: 14px;
  }
}
</style>